import type {
    LiveControlWriteRequestTO,
    VehicleAttributeKeyTO as VehicleAttributeKeyTOType,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import { liveControlsApi, managementApi } from 'features/domain-simulation/apiClient';
import { useCallback } from 'react';

interface Options {
    onSuccess?: () => void;
}

const useSimulationActions = ({ onSuccess }: Options = {}) => {
    const start = useCallback(
        async (vin: string) => {
            try {
                await managementApi.startSimulation(vin);
                onSuccess?.();
            } catch (error) {
                console.error(error);
            }
        },
        [onSuccess]
    );

    const remove = useCallback(
        async (vin: string) => {
            try {
                await managementApi.deleteSimulation(vin);
                onSuccess?.();
            } catch (error) {
                console.error(error);
            }
        },
        [onSuccess]
    );

    const setConnectivity = useCallback(
        async (vin: string, isOnline: boolean) => {
            try {
                await liveControlsApi.setConnectivity(vin, { isOnline, graceful: false });
                onSuccess?.();
            } catch (error) {
                console.error(error);
            }
        },
        [onSuccess]
    );

    const triggerMrm = useCallback(
        async (vin: string) => {
            try {
                await liveControlsApi.triggerVehicleMrm(vin);
                onSuccess?.();
            } catch (error) {
                console.error(error);
            }
        },
        [onSuccess]
    );

    const writeAttributes = useCallback(
        async (vin: string, body: LiveControlWriteRequestTO) => {
            try {
                await liveControlsApi.writeAttribute(vin, body);
                onSuccess?.();
            } catch (error) {
                console.error(error);
            }
        },
        [onSuccess]
    );

    const pinAttribute = useCallback(
        async (vin: string, attribute: VehicleAttributeKeyTOType) => {
            try {
                await liveControlsApi.pinAttribute(vin, attribute);
                onSuccess?.();
            } catch (error) {
                console.error(error);
            }
        },
        [onSuccess]
    );

    const unpinAttribute = useCallback(
        async (vin: string, attribute: VehicleAttributeKeyTOType) => {
            try {
                await liveControlsApi.unpinAttribute(vin, attribute);
                onSuccess?.();
            } catch (error) {
                console.error(error);
            }
        },
        [onSuccess]
    );

    return { start, remove, setConnectivity, triggerMrm, writeAttributes, pinAttribute, unpinAttribute };
};

export default useSimulationActions;
